import React, { useState } from 'react';
import { get, uniqueId } from 'lodash';
import {
  SdfButton,
  SdfBox,
  SdfExpandableBox,
  SdfLink,
  SdfTooltip,
  SdfShimmer
} from '@synerg/react-components';
import { setLoadings, useApplicationState, updateCurrentProjectDetails } from '../../../utilities/applicationState';
import './projectCredentialsStyle.scss';
import CopyToClipboard from 'react-copy-to-clipboard';
import { CredentialsBox } from './credentialsBox';

export const ProjectCredentials = (props = {}) => {
  const { getClientSecret } = props;
  const [store, dispatch] = useApplicationState();
  const { loadings, projects: { current } } = store;
  const { devInfo, projectCarDetails = {}, b2bClientId, b2cClientId, b2bClientSecret, b2cClientSecret, showB2bSecret = false, showB2cSecret = false } = current;
  const devDataCloudProject = get(devInfo, 'platform', '') === 'datacloud';
  const endUserRedirectUrl = !devDataCloudProject && get(projectCarDetails, 'b2cRedirectLink.href', '');
  const [isStepOpen, setIsStepOpen] = useState({ 0: true, 1: true });
  const { isLoadingB2cSecret = false, isLoadingB2bSecret = false, loadingDevClientCredentials = true, loadingAuthorizationCode = true, isLoadingCarDetails = true } = loadings;
  const b2bConsumerAppID = get(devInfo, 'b2bConsumerAppID', '');
  const b2cConsumerAppID = get(devInfo, 'b2cConsumerAppID', '');
  const dataConnectorID = get(devInfo, "dataConnectorID", '');
  const endUserAppID = get(devInfo, "endUserAppID", '');


  const onStepChanged = (step, status) => {
    setIsStepOpen((s) => ({
      ...s,
      ...({ [step]: status === "open" })
    }));
  };


  const toggleShowSecret = (toggled, clientType) => {
    if (clientType === 'B2b') {
      dispatch(updateCurrentProjectDetails({ showB2bSecret: toggled }));
      if (toggled === false) {
        dispatch(updateCurrentProjectDetails({ b2bClientSecret: null }));
      } else if (toggled === true && devInfo._id) {
        getDevSecrets(clientType);
      }
    }
    if (clientType === 'B2c') {
      dispatch(updateCurrentProjectDetails({ showB2cSecret: toggled }));
      if (toggled === false) {
        dispatch(updateCurrentProjectDetails({ b2cClientSecret: null }));
      } else if (toggled === true && devInfo._id) {
        getDevSecrets(clientType);
      }
    }
  };


  const getDevSecrets = (clientType) => {
    // Client Credentials B2b
    if (clientType === 'B2b' && b2bClientId !== 'Not available') {
      dispatch(setLoadings({ isLoadingB2bSecret: true }));

      getClientSecret(b2bConsumerAppID, devInfo._id)
        .then(b2bs => {
          dispatch(updateCurrentProjectDetails({ b2bClientSecret: b2bs }));
        })
        .catch(() => {
          dispatch(updateCurrentProjectDetails({ b2bClientSecret: null }));
        })
        .finally(() => {
          dispatch(setLoadings({ isLoadingB2bSecret: false }));
        });
    }
    // Authorization Code
    if (clientType === 'B2c' && b2cClientId !== 'Not available') {
      dispatch(setLoadings({ isLoadingB2cSecret: true }));

      getClientSecret(b2cConsumerAppID, devInfo._id)
        .then(b2cs => {
          dispatch(updateCurrentProjectDetails({ b2cClientSecret: b2cs }));
        })
        .catch(() => {
          dispatch(updateCurrentProjectDetails({ b2cClientSecret: null }));
        })
        .finally(() => {
          dispatch(setLoadings({ isLoadingB2cSecret: false }));
        });
    }
  };



  return (
    <div className="block w-full">
      <div className="text-heading-05 font-bold w-full">Credentials</div>

      {b2bConsumerAppID &&
        <div className="w-full mt-5">
          <SdfExpandableBox
            opened={isStepOpen[0]}
            onSdfClose={() => {
              onStepChanged(0, "close");
            }}
            onSdfOpen={() => {
              onStepChanged(0, "open");
            }}
            header="Data connector credentials"
            triggerPlacement="after"
            variant="bordered"
          >
            <SdfBox className="mt-6" variant="filled" spacing="default">
              <CredentialsBox
                clientType="B2b"
                clientId={b2bClientId}
                clientSecret={b2bClientSecret}
                showSecret={showB2bSecret}
                loadingClientId={loadingDevClientCredentials}
                loadingSecret={isLoadingB2bSecret}
                toggleShowSecret={toggleShowSecret}
              />

            </SdfBox>
          </SdfExpandableBox>
        </div>
      }

      {b2cConsumerAppID &&
        <div className="w-full mt-5 mb-6">
          <SdfExpandableBox
            opened={isStepOpen[1]}
            onSdfClose={() => {
              onStepChanged(1, "close");
            }}
            onSdfOpen={() => {
              onStepChanged(1, "open");
            }}
            header="End-user/SSO credentials"
            triggerPlacement="after"
            variant="bordered"
          >
            <SdfBox className="mt-6" variant="filled" spacing="default">
              <CredentialsBox
                clientType="B2c"
                clientId={b2cClientId}
                clientSecret={b2cClientSecret}
                showSecret={showB2cSecret}
                loadingClientId={loadingAuthorizationCode}
                loadingSecret={isLoadingB2cSecret}
                toggleShowSecret={toggleShowSecret}
              />

              <div className="flex items-center justify-between mt-6 flex-wrap">
                <div className="w-3/12 pr-4 font-bold">
                  End-User App Redirect URL
                </div>
                {isLoadingCarDetails ? <SdfShimmer className='w-sm' /> :
                  <div className="pr-24">
                    {endUserRedirectUrl ? <SdfLink href={endUserRedirectUrl} target="_blank">
                      {endUserRedirectUrl}
                    </SdfLink> : "NA"}
                  </div>}
              </div>
            </SdfBox>
          </SdfExpandableBox>
        </div>
      }

      <div className="text-heading-03 font-bold w-full mt-16">
        Application IDs
      </div>

      {b2bConsumerAppID &&
        <div className="w-full mt-5">
          <SdfBox variant="bordered" spacing="default">
            <div className="flex items-center">
              <div className="flex-1 font-bold">Data connector ID</div>
              <div className="pl-6 pr-6">
                {dataConnectorID}
              </div>

              {dataConnectorID ?
                <CopyToClipboard text={dataConnectorID}>
                  <SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
                    <SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
                    Copied
                  </SdfTooltip>
                </CopyToClipboard>
                :
                <SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
              }

            </div>
          </SdfBox>
        </div>
      }

      {b2cConsumerAppID &&
        <div className="w-full mt-5 mb-6">
          <SdfBox variant="bordered" spacing="default">
            <div className="flex items-center">
              <div className="flex-1 font-bold">
                End-user/SSO application ID
              </div>
              <div className="pl-6 pr-6">
                {endUserAppID}
              </div>

              {endUserAppID ?
                <CopyToClipboard text={endUserAppID}>
                  <SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
                    <SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
                    Copied
                  </SdfTooltip>
                </CopyToClipboard>
                :
                <SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
              }
            </div>
          </SdfBox>
        </div>
      }
    </div>
  );
};
